import moment from 'moment';
import rootStore from '../../RootStore';
import {
    E_RISK_TYPE,
    GLOBAL_INDEXES,
    RISK_CHART_MODES,
} from '../../interfaces';
import { COMPOSITE_DATA_INTERVAL } from '../../components/GQChart/ChartStore';
import { HeatmapScoreType } from '../../components/Heatmap/HeatmapTypes';
import {
    abbrToRiskId,
    countryIdToIso,
    commodityToIndicator,
    decodeCountryList,
    encodeCountryList,
    indicatorToCommodity,
    indicatorToMacro,
    isoToCountryId,
    macroToIndicator,
    riskIdToAbbr,
} from './resolvers';

const DATE_FORMAT = 'YYYY-MM-DD';

export function encodeDateRange(): { from: string; to: string } {
    const { baseDate, rangeLeft, rangeRight } = rootStore.chartStore;
    const start = baseDate.clone().subtract(rangeLeft, 'days');
    const end = baseDate.clone().add(rangeRight, 'days');
    return { from: start.format(DATE_FORMAT), to: end.format(DATE_FORMAT) };
}

export function decodeDateRange(from?: string, to?: string): void {
    if (!from || !to) {
        return;
    }
    const start = moment(from, DATE_FORMAT, true);
    const end = moment(to, DATE_FORMAT, true);
    if (!start.isValid() || !end.isValid() || end.diff(start, 'days') < 1) {
        return;
    }
    rootStore.chartStore.setNewRangeByDates(start.toDate(), end.toDate());
}

export function encodeDate(date: Date): string {
    return moment(date).format(DATE_FORMAT);
}

export function decodeDate(value?: string): Date {
    if (!value) {
        return null;
    }
    const parsed = moment(value, DATE_FORMAT, true);
    return parsed.isValid() ? parsed.toDate() : null;
}

export function encodeVirtualToday(): string {
    return rootStore.chartStore.virtualToday.format(DATE_FORMAT);
}

export function decodeVirtualToday(value?: string): void {
    if (!value) {
        return;
    }
    const parsed = moment(value, DATE_FORMAT, true);
    if (!parsed.isValid()) {
        return;
    }
    rootStore.chartStore.setVirtualToday(parsed);
}

export function encodeFrequency(): string {
    return rootStore.chartStore.compositeDataInterval;
}

export function decodeFrequency(value?: string): void {
    if (
        value === COMPOSITE_DATA_INTERVAL.DAILY ||
        value === COMPOSITE_DATA_INTERVAL.MONTHLY
    ) {
        rootStore.chartStore.compositeDataInterval = value;
    }
}

export function encodeCharts(): string {
    const { riskChartMode, projectionDataOn, riskMarkersOn } = rootStore.risksStore;
    const parts: string[] = [];
    if (
        riskChartMode === RISK_CHART_MODES.RISK ||
        riskChartMode === RISK_CHART_MODES.RISK_AND_PULSE
    ) {
        parts.push('composite');
    }
    if (
        riskChartMode === RISK_CHART_MODES.PULSE ||
        riskChartMode === RISK_CHART_MODES.RISK_AND_PULSE
    ) {
        parts.push('pulse');
    }
    if (projectionDataOn) {
        parts.push('projections');
    }
    if (riskMarkersOn) {
        parts.push('hotspots');
    }
    return parts.join(',');
}

export function decodeCharts(value?: string): void {
    if (!value) {
        return;
    }
    const set = new Set(value.split(',').map(s => s.trim().toLowerCase()));
    const composite = set.has('composite');
    const pulse = set.has('pulse');
    let riskChartMode: RISK_CHART_MODES;
    if (composite && pulse) {
        riskChartMode = RISK_CHART_MODES.RISK_AND_PULSE;
    } else if (pulse) {
        riskChartMode = RISK_CHART_MODES.PULSE;
    } else {
        // default / composite-only
        riskChartMode = RISK_CHART_MODES.RISK;
    }
    rootStore.risksStore.setState({
        riskChartMode,
        projectionDataOn: set.has('projections'),
        riskMarkersOn: set.has('hotspots'),
    });
}

export function encodeRiskIds(): string {
    const type = rootStore.risksStore.currentRisksType;
    return rootStore.risksStore.currentList
        .map(id => riskIdToAbbr(type, id))
        .filter((abbr): abbr is string => Boolean(abbr))
        .join(',');
}

export function decodeRiskIds(value?: string): void {
    if (value == null) {
        return;
    }
    const type = rootStore.risksStore.currentRisksType;
    const ids = value
        .split(',')
        .map(token => abbrToRiskId(type, token))
        .filter((id): id is number => id != null);
    const store = rootStore.risksStore.currentStore;
    if (store) {
        store.overrideValues(...ids);
    }
}

export function encodeSingleRiskId(): string {
    const type = rootStore.risksStore.currentRisksType;
    const [first] = rootStore.risksStore.currentList;
    return first != null ? riskIdToAbbr(type, first) ?? '' : '';
}

export function decodeSingleRiskId(value?: string): void {
    if (!value) {
        return;
    }
    const type = rootStore.risksStore.currentRisksType;
    const id = abbrToRiskId(type, value.trim());
    if (id == null) {
        return;
    }
    const store = rootStore.risksStore.currentStore;
    if (store) {
        store.overrideValues(id);
    }
}

export function encodeCompare(): string {
    const risks = rootStore.risksStore;
    if (risks.currencyOn && risks.currencyCountry?.[0] != null) {
        const iso = countryIdToIso(risks.currencyCountry[0]);
        if (iso) {
            return `currency:${iso}`;
        }
    }
    if (risks.macroeconomicsCountry?.[0] != null) {
        const macro = indicatorToMacro(risks.activeMacroeconomicsIndicator);
        const iso = countryIdToIso(risks.macroeconomicsCountry[0]);
        if (macro && iso) {
            return `macro:${macro}:${iso}`;
        }
    }
    const commodity = (rootStore.risksStore.activeGlobalIndicators || [])
        .map(indicatorToCommodity)
        .find(Boolean);
    if (commodity) {
        return `commodity:${commodity}`;
    }
    return '';
}

export function decodeCompare(value?: string): void {
    if (!value) {
        return;
    }
    const [kind, a, b] = value.split(':');
    const risks = rootStore.risksStore;
    switch (kind) {
        case 'currency': {
            const id = isoToCountryId(a);
            if (id != null) {
                risks.setCurrencyCountries([id]);
            }
            return;
        }
        case 'commodity': {
            const indicator = commodityToIndicator(a);
            if (indicator) {
                risks.overrideGlobalIndicators([indicator]);
            }
            return;
        }
        case 'macro': {
            const indicator: GLOBAL_INDEXES = macroToIndicator(a);
            const countryId = isoToCountryId(b);
            if (indicator && countryId != null) {
                risks.setMacroeconomicsCountry(countryId, indicator);
            }
            return;
        }
        default:
            return;
    }
}

const HEATMAP_SCORE_TO_TOKEN: Partial<Record<HeatmapScoreType, string>> = {
    [HeatmapScoreType.RISK]: 'risk',
    [HeatmapScoreType.PULSE]: 'pulse',
    [HeatmapScoreType.PULSE_INTENSITY]: 'pulse_intensity',
};
const TOKEN_TO_HEATMAP_SCORE: Record<string, HeatmapScoreType> = {
    risk: HeatmapScoreType.RISK,
    pulse: HeatmapScoreType.PULSE,
    pulse_intensity: HeatmapScoreType.PULSE_INTENSITY,
};

export function encodeHeatmapScore(): string {
    return HEATMAP_SCORE_TO_TOKEN[rootStore.heatmapStore.scoreType] ?? '';
}
export function decodeHeatmapScore(value?: string): void {
    const score = TOKEN_TO_HEATMAP_SCORE[(value || '').toLowerCase()];
    if (score) {
        rootStore.heatmapStore.setScoreType(score);
    }
}

const RISK_TYPE_TO_TOKEN: Partial<Record<E_RISK_TYPE, string>> = {
    [E_RISK_TYPE.DIMENSIONS]: 'fundamentals',
    [E_RISK_TYPE.CLIENT_FACING_INDICATORS]: 'focus',
};
const TOKEN_TO_RISK_TYPE: Record<string, E_RISK_TYPE> = {
    fundamentals: E_RISK_TYPE.DIMENSIONS,
    focus: E_RISK_TYPE.CLIENT_FACING_INDICATORS,
};

export function encodeHeatmapRiskType(): string {
    return RISK_TYPE_TO_TOKEN[rootStore.heatmapStore.riskType] ?? '';
}
export function decodeHeatmapRiskType(value?: string): void {
    const type = TOKEN_TO_RISK_TYPE[(value || '').toLowerCase()];
    if (type != null) {
        rootStore.heatmapStore.setRiskType(type);
    }
}

export function encodeHeatmapCountries(): string {
    const selected = rootStore.heatmapStore.countriesList || [];
    const licensed = rootStore.countryStore.countries?.licensedCountriesArr ?? [];
    if (licensed.length > 0 && selected.length === licensed.length) {
        return 'All';
    }
    return encodeCountryList(selected);
}

export function decodeHeatmapCountries(value?: string): void {
    if (!value) {
        return;
    }
    if (value.trim().toLowerCase() === 'all') {
        const licensed =
            rootStore.countryStore.countries?.licensedCountriesArr ?? [];
        rootStore.heatmapStore.setCountriesList(licensed.map(c => c.id));
        return;
    }
    rootStore.heatmapStore.setCountriesList(decodeCountryList(value));
}

export function encodeHeatmapHighlightedCountries(): string {
    const { selectedCountry } = rootStore.heatmapStore;
    if (selectedCountry == null) {
        return '';
    }
    const iso = countryIdToIso(selectedCountry);
    return iso ?? '';
}

export function decodeHeatmapHighlightedCountries(value?: string): void {
    if (!value) {
        return;
    }
    const firstIso = value.split(',')[0];
    const id = isoToCountryId(firstIso);
    if (id != null) {
        rootStore.heatmapStore.setSelectedCountry(id);
    }
}

export function encodeHeatmapHighlightedRisks(): string {
    const { selectedRisk, riskType } = rootStore.heatmapStore;
    if (selectedRisk == null) {
        return '';
    }
    return riskIdToAbbr(riskType, selectedRisk) ?? '';
}

export function decodeHeatmapHighlightedRisks(value?: string): void {
    if (!value) {
        return;
    }
    const firstAbbr = value.split(',')[0];
    const riskId = abbrToRiskId(rootStore.heatmapStore.riskType, firstAbbr);
    if (riskId != null) {
        rootStore.heatmapStore.setSelectedRisk(riskId);
    }
}

export { encodeCountryList, decodeCountryList, countryIdToIso, isoToCountryId };
